/* eslint-disable no-console */
import { Culling, WaitForPassSeriesEndMode } from './atlasCulling.js';

/**
 * Class represents an Atlas Graphics Loader.
 *
 * @param {object} atlasCVFLoader - The Atlas CVF Loader.
 * @param {object} atlasSelTools - The Atlas Selection Tools (deprecated).
 * @param {object} viewManager - The View Manager
 * @param {object} pmiManager - The PMI Manager (deprecated).
 * @param {boolean} testMode - The test mode flag.
 */
function atlasGraphicsLoader(
    atlasCVFLoader,
    atlasSelTools = null /* deprecated */,
    viewManager,
    pmiManager = null /* deprecated */,
    testMode = false
) {
    this.m_atlasCVFLoader = atlasCVFLoader;
    this.m_viewManager = viewManager;
    this.m_pmiManager = viewManager.pmi;
    this.m_ignoreCamera = false;
    this.m_ignoreViewDecoration = false;
    this.m_graphicMap = new Map();
    this.m_loadInterval = undefined;
    this.m_dispInterval = undefined;
    this.m_bufferSize = 0;
    this.m_modelRootPsId = new Set();
    this.m_modelRootPsIdPromises = [];
    this.m_loadtime = undefined;
    this.m_rendtime = undefined;
    this.m_structureBuffer = 0;
    this.m_geometryBuffer = 0;
    this.m_key = 0;
    this.m_deleteInterval = false;
    this.m_maxBuffer = 4000000;
    this.m_statistic = false;
    this.m_loaderParamsQuery;
    this.m_loaderParamsQueryData = {};
    this.m_showPMIs = true;
    this.m_hasPMIs = false;
    this.m_testMode = testMode;
    this.m_viewLoaded = false;

    let m_eventListeners = undefined;

    // Init the loader, ensure user can get the loader from the viewer before the first graphics arrives.
    //
    if (this.m_viewManager.getLoader() !== this.m_atlasCVFLoader) {
        this.m_viewManager.setLoader(this.m_atlasCVFLoader);
    }

    atlasGraphicsLoader.prototype.reset = () => {
        this.m_ignoreCamera = false;
        this.m_ignoreViewDecoration = false;
        this.m_graphicMap.clear();
        this.m_key = 0;
        this.m_modelRootPsId.clear();

        if (this.m_loadInterval !== undefined) {
            clearInterval(this.m_loadInterval);
        }
        this.m_loadInterval = undefined;

        if (this.m_dispInterval !== undefined) {
            clearInterval(this.m_dispInterval);
        }
        this.m_dispInterval = undefined;

        this.m_bufferSize = 0;
        this.m_loadtime = undefined;
        this.m_rendtime = undefined;
        this.m_structureBuffer = 0;
        this.m_geometryBuffer = 0;
        this.m_loaderParamsQueryData = {};
        this.m_hasPMIs = false;
        this.m_modelRootPsIdPromises.length = 0;
        this.m_viewLoaded = false;
    };

    atlasGraphicsLoader.prototype.intervalLoad = () => {
        if (this.m_graphicMap.size > 0) {
            let mapEntry = this.m_graphicMap.entries().next();
            let child = mapEntry.value[1];
            if (child.length === 2) {
                let graphics = {};
                graphics.children = child;

                try {
                    // <skarmalk>
                    // Delete map entry first in case there is error encountered during processing.
                    if (!this.m_graphicMap.delete(mapEntry.value[0])) {
                        this.raiseError('Failed to remove graphics for key', mapEntry.value[0]);
                    }

                    // <Felix Fang>
                    // Graphics has to load in sequential order to avoid data conflict.
                    Promise.all(this.m_modelRootPsIdPromises).then(() => {
                        this.m_modelRootPsIdPromises.length = 0;
                        let loadPromise = new Promise((resolve) => {
                            this.load3DMemoryDataAsync(graphics).then(() => {
                                // Only ignore camera and view decoration when there is root node.
                                if (this.m_viewLoaded === true) {
                                    this.m_ignoreCamera = true;
                                    this.m_ignoreViewDecoration = true;
                                }
                                resolve();
                            });
                        });
                        this.m_modelRootPsIdPromises.push(loadPromise);
                    });
                } catch (err) {
                    if (this.m_viewLoaded === true) {
                        this.m_ignoreCamera = true;
                        this.m_ignoreViewDecoration = true;
                    }
                    this.raiseError('Error during load structure', err);
                }
            }
        }
    };

    /**
     * Adds structure to the Atlas Graphics Loader.
     *
     * @param {object} data - The structure data.
     * @param {object} header - The structure header.
     */
    atlasGraphicsLoader.prototype.addStructure = (data, header) => {
        let children = [];
        children.push(data.buffer);
        this.m_graphicMap.set(this.m_key, children);

        this.m_structureBuffer += data.byteLength;

        if (this.m_loadInterval === undefined) {
            this.m_loadInterval = setInterval(() => {
                this.intervalLoad();
            }, 0);
        }
    };

    /**
     * Adds geometry to the Atlas Graphics Loader.
     *
     * @param {object} data - The geometry data.
     * @param {object} header - The geometry header.
     */
    atlasGraphicsLoader.prototype.addGeometry = (data, header) => {
        let children = this.m_graphicMap.get(this.m_key);
        this.m_key++;
        if (children !== undefined) {
            this.m_geometryBuffer += data.byteLength;
            if (this.m_dispInterval === undefined) {
                this.m_dispInterval = setInterval(() => {
                    this.setVisibility(false);
                }, 1000);
            }
            children.push(data.buffer);
        } else {
            this.raiseError('Key not found', this.m_key);
        }
    };

    atlasGraphicsLoader.prototype.load3DMemoryDataAsync = async (graphics) => {
        if (!Array.isArray(graphics.children) || graphics.children.length !== 2) {
            this.raiseError('Invalid graphics data for ', graphics);
            return;
        }

        this.m_bufferSize += graphics.children[1].byteLength;
        let graphicsStart = performance.now();
        if (this.m_loadtime === undefined) {
            this.m_loadtime = 0;
        }
        if (this.m_viewManager.getLoader() !== this.m_atlasCVFLoader) {
            this.m_viewManager.setLoader(this.m_atlasCVFLoader);
        }

        this.m_atlasCVFLoader.addEventListener('viewLoaded', () => {
            this.m_viewLoaded = true;
        });

        if (!this.m_ignoreCamera) {
            if (typeof this.m_loaderParamsQuery === 'function') {
                this.m_loaderParamsQuery(this.m_loaderParamsQueryData);
            }
        }

        graphics.ignoreCamera = this.m_ignoreCamera;
        graphics.ignoreViewDecoration = this.m_ignoreViewDecoration;

        for (let prop in this.m_loaderParamsQueryData) {
            graphics[prop] = this.m_loaderParamsQueryData[prop];
        }

        if (this.m_statistic) {
            console.info('PLMVisWeb Geometry buffer size', graphics.children[0].byteLength);
            console.info('PLMVisWeb Structure buffer size', graphics.children[1].byteLength);
        }

        // Loading the data
        //
        let { psId, hasPmi, hasProtoOnly } = await this.m_atlasCVFLoader.load(graphics);

        // PR10507997 Set PMI visibility unconditionally if PMI exists in one of the CVF message.
        //
        this.m_hasPMIs = this.m_hasPMIs === true || hasPmi === true;

        let loadTime = performance.now() - graphicsStart;
        this.m_loadtime += loadTime;
        if (this.m_statistic) {
            console.info('CVFLoader load time: ', this.getFormattedTime(loadTime));
        }

        // <TODO> Add logic to handle prototype node caching
        if (hasProtoOnly !== true) {
            this.m_modelRootPsId.add(psId);
        }
    };

    atlasGraphicsLoader.prototype.setVisibility = (force) => {
        if (this.m_bufferSize > this.m_maxBuffer || force) {
            this.m_bufferSize = 0;
            if (this.m_modelRootPsId.size > 0) {
                let graphicsStart = performance.now();
                if (this.m_rendtime === undefined) {
                    this.m_rendtime = 0;
                }

                let hasPMI = this.m_hasPMIs === true;
                let cullingManager = this.getCullingManager();
                if (cullingManager.isEnabled() && force) {
                    let scanner = cullingManager.getScanner();
                    scanner.isActive = true;

                    // m_modelRootPsId can be flushed while scanning.
                    // Copy to a different array so we won't lose it in the culling callback.
                    //
                    let modelRootPsId = Array.from(this.m_modelRootPsId);
                    modelRootPsId.forEach((key) => {
                        this.m_viewManager.setVisibilityByPsId(key, true, () => {
                            scanner.waitForPassSeriesEnd(WaitForPassSeriesEndMode.WAIT_EVEN_IF_NOT_STARTED_YET, () => {
                                if (hasPMI === true) {
                                    this.loadExtraModelData(key);
                                }
                            });
                            dispatchEvent(GraphicsLoaderEventType.postSetVisibilityEvent);
                        });
                        console.log(`${key} viewer manager is successful`);
                    });
                } else {
                    this.m_modelRootPsId.forEach((key) => {
                        this.m_viewManager.setVisibilityByPsId(key, true, () => {
                            if (hasPMI === true) {
                                this.loadExtraModelData(key);
                            }
                            dispatchEvent(GraphicsLoaderEventType.postSetVisibilityEvent);
                        });
                        console.log(`${key} viewer manager is successful`);
                        this.m_modelRootPsId.delete(key);
                    });
                }

                let rendtime = 0;
                if (this.hasRootNode()) {
                    this.m_fit(this.m_rendtime === 0, force);
                    rendtime = performance.now() - graphicsStart;
                    this.m_rendtime += rendtime;
                }

                if (this.m_statistic) {
                    console.info('PLMVisWeb render time: ', this.getFormattedTime(rendtime));
                }
            }
        }
    };

    atlasGraphicsLoader.prototype.endGraphicLoad = (endCallback, modelId) => {
        let hasGraphics = this.loadAll(() => {
            this.setVisibility(true);

            // Workaround of: https://gitlab.industrysoftware.automation.siemens.com/PLMVisWeb/InternalCustomerCenter/-/issues/1555#note_5380503
            // For performance reason, this workaround is moving to this module instead.
            if (this.m_pmiManager !== undefined && this.m_pmiManager !== null) {
                let mvId = this.m_pmiManager.activeMVPsId;

                if (mvId !== null && mvId !== undefined && this.m_pmiManager.setModelViewActive !== undefined) {
                    let oldAutoDraw = this.m_viewManager.autoDraw;
                    // Disable autoDraw to avoid on-off flushing
                    this.m_viewManager.autoDraw = false;
                    this.m_pmiManager.setModelViewActive(mvId, false);
                    this.m_pmiManager.setModelViewActive(mvId, true);
                    this.m_viewManager.autoDraw = oldAutoDraw;
                    if (oldAutoDraw === true) {
                        this.m_viewManager.draw();
                    }
                }
            }

            if (this.m_loadtime !== undefined && (this.m_statistic || this.m_testMode !== undefined)) {
                console.info('Total CVFLoader load time: ', this.getFormattedTime(this.m_loadtime));
                console.info('Total PLMVisWeb render time: ', this.getFormattedTime(this.m_rendtime));
                console.info('Total PLMVisWeb Structure buffer size: ', this.m_structureBuffer);
                console.info('Total PLMVisWeb Geometry buffer size: ', this.m_geometryBuffer);
            }
            let cullingManager = this.getCullingManager();
            if (cullingManager.isEnabled()) {
                cullingManager.onEndPartGeometryLoad();
            }
            endCallback();

            this.reset();
        }, modelId);

        // <Felix Fang>
        // Reset when there is no graphics since loadAll callback will not invoked.
        if (hasGraphics === false) {
            this.reset();
        }
    };

    atlasGraphicsLoader.prototype.loadAll3DMemoryDataPromise = async (modelId) => {
        // <Felix Fang>
        // Graphics has to be loaded in sequential order to avoid data racing
        for (let [key, value] of this.m_graphicMap) {
            let graphics = {
                children: value,
                modelId: modelId
            };
            try {
                // eslint-disable-next-line no-await-in-loop
                await this.load3DMemoryDataAsync(graphics);
            } catch (err) {
                this.raiseError('Error during load structure', err);
            }

            // Only ignore camera and view decoration when there is root node.
            if (this.m_viewLoaded === true) {
                this.m_ignoreCamera = true;
                this.m_ignoreViewDecoration = true;
            }
        }

        this.m_graphicMap.clear();
    };

    atlasGraphicsLoader.prototype.loadAll = (callback, modelId) => {
        let hasGraphics = true;
        if (this.m_loadInterval !== undefined) {
            clearInterval(this.m_loadInterval);
        }
        if (this.m_dispInterval !== undefined) {
            clearInterval(this.m_dispInterval);
        }
        this.m_loadInterval = undefined;
        this.m_dispInterval = undefined;

        // <Felix Fang>
        // Process pending graphics data. Data must be loaded in sequential order
        // due to order dependency in contents; and to avoid race conditions.
        if (this.m_modelRootPsIdPromises.length > 0 || this.m_graphicMap.size > 0) {
            Promise.all(this.m_modelRootPsIdPromises).then((v) => {
                this.m_modelRootPsIdPromises.length = 0;
                this.loadAll3DMemoryDataPromise(modelId).then(() => {
                    callback();
                });
            });
        }

        // It may so happen, that there is nothing pending load; but
        // display call has not happened yet. Since m_dispInterval has
        // been cleared above, it won't trigger. Hence, trigger callback
        // to ensure that model is displayed.
        //
        else if (this.m_modelRootPsId.size > 0) {
            callback();
        } else {
            hasGraphics = false;
        }

        return hasGraphics;
    };

    atlasGraphicsLoader.prototype.hasRootNode = () => {
        if (this.m_viewManager === undefined) {
            return false;
        }
        let psInfo = this.m_viewManager.getProductStructureInfo('-1', [], 0, false);
        if (psInfo && Array.isArray(psInfo.childrenIds) && psInfo.childrenIds.length > 0) {
            return true;
        }
        return false;
    };

    atlasGraphicsLoader.prototype.setShowPMIs = (showPMIs) => {
        this.m_showPMIs = showPMIs;
    };

    atlasGraphicsLoader.prototype.setStatistic = (flag) => {
        this.m_statistic = flag;
    };

    // Set the max buffer to trigger a graphic display
    atlasGraphicsLoader.prototype.setMaxBuffer = (buffer) => {
        this.m_maxBuffer = buffer;
    };

    atlasGraphicsLoader.prototype.fit = (firstTime, lastTime) => {
        try {
            if (this.m_viewManager !== undefined && (firstTime || lastTime)) {
                this.m_viewManager.fitToVisible();
            }
        } catch (err) {
            this.raiseError(err);
        }
    };

    atlasGraphicsLoader.prototype.setFit = (fit) => {
        this.m_fit = fit;
    };

    atlasGraphicsLoader.prototype.registerLoaderParamsQuery = (callback) => {
        this.m_loaderParamsQuery = callback;
    };

    atlasGraphicsLoader.prototype.getFormattedTime = (time) => {
        let d = new Date(time);
        return `${d.getUTCHours()}:${d.getUTCMinutes()}:${d.getUTCSeconds()}:${d.getUTCMilliseconds()}`;
    };

    atlasGraphicsLoader.prototype.getLoadTime = () => {
        return this.m_loadtime;
    };

    atlasGraphicsLoader.prototype.getRendTime = () => {
        return this.m_rendtime;
    };

    atlasGraphicsLoader.prototype.getCullingManager = () => {
        if (this.m_cullingManager === undefined) {
            this.m_cullingManager = new Culling(this);
        }
        return this.m_cullingManager;
    };

    atlasGraphicsLoader.prototype.loadExtraModelData = (psId) => {
        // Load PMIs
        if (this.m_pmiManager !== undefined && this.m_pmiManager !== null) {
            this.m_pmiManager.loadPmiData(psId, (success) => {
                if (this.m_showPMIs === true) {
                    this.m_pmiManager.setVisibilityByPsId('-3', true, () => {});
                }
            });
        }
    };

    atlasGraphicsLoader.prototype.addEventListener = (eventName, listener) => {
        // Ignore invalid event name
        if (eventName === undefined || !GraphicsLoaderEventType.hasOwnProperty(eventName)) {
            console.warn(`AtlasGraphicsLoader event: ${eventName} is not supported.`);
            return;
        }

        if (m_eventListeners === undefined) {
            m_eventListeners = {};
        }

        if (m_eventListeners[eventName] === undefined) {
            m_eventListeners[eventName] = [];
        }

        if (m_eventListeners[eventName].indexOf(listener) === -1) {
            m_eventListeners[eventName].push(listener);
        }
    };

    atlasGraphicsLoader.prototype.removeEventListener = (eventName, listener) => {
        // Ignore invalid event name
        if (eventName === undefined || !GraphicsLoaderEventType.hasOwnProperty(eventName)) {
            console.warn(`AtlasGraphicsLoader event: ${eventName} does not exist.`);
            return;
        }

        if (m_eventListeners === undefined) {
            m_eventListeners = {};
        }

        if (m_eventListeners[eventName] === undefined) {
            m_eventListeners[eventName] = [];
        }

        if (listener === undefined) {
            m_eventListeners[eventName] = [];
        } else {
            let index = m_eventListeners[eventName].indexOf(listener);

            if (index !== -1) {
                m_eventListeners[eventName].splice(index, 1);
            }
        }
    };

    atlasGraphicsLoader.prototype.raiseError = () => {
        if (this.OnError !== undefined) {
            this.OnError(...arguments);
        } else {
            console.error(...arguments);
        }
    };

    this.m_fit = this.fit;

    const dispatchEvent = (eventName, ...args) => {
        if (eventName === null || eventName === undefined) {
            return;
        }

        if (m_eventListeners === undefined || typeof m_eventListeners !== 'object') {
            return;
        }

        let listeners = m_eventListeners[eventName];
        if (listeners === undefined || !(listeners instanceof Array)) {
            return;
        }

        for (let listener of listeners) {
            listener(...args);
        }
    };
}

export const GraphicsLoaderEventType = Object.freeze({
    postSetVisibilityEvent: 'postSetVisibilityEvent'
});

export { atlasGraphicsLoader };
